'use client';

import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

type ContactField = 'name' | 'email' | 'subject' | 'message';

type ContactFormValues = Record<ContactField, string>;
type ContactFormErrors = Partial<Record<ContactField, string>>;

const SUBJECT_OPTIONS = [
  { value: 'general', label: 'Общий вопрос' },
  { value: 'support', label: 'Техническая поддержка' },
  { value: 'sales', label: 'Продажи и тарифы' },
  { value: 'partnership', label: 'Партнерство' },
  { value: 'feedback', label: 'Обратная связь' },
];

const INITIAL_VALUES: ContactFormValues = {
  name: '',
  email: '',
  subject: '',
  message: '',
};

const selectClassName =
  'flex h-[var(--site-control-height)] w-full rounded-[var(--site-input-radius)] border border-[color:var(--site-border-soft)] bg-[var(--site-surface-strong)] px-[var(--site-input-px)] py-[var(--site-input-py)] text-[length:var(--site-input-font-size)] shadow-[var(--site-shadow-soft)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

function validateContactForm(values: ContactFormValues): ContactFormErrors {
  const errors: ContactFormErrors = {};

  if (values.name.trim().length < 2) {
    errors.name = 'Имя должно содержать минимум 2 символа';
  }

  const email = values.email.trim();
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Введите корректный адрес электронной почты';
  }

  if (!values.subject.trim()) {
    errors.subject = 'Выберите тему обращения';
  }

  if (values.message.trim().length < 10) {
    errors.message = 'Сообщение должно содержать минимум 10 символов';
  }

  return errors;
}

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null;
  }

  return <p className="text-sm text-destructive">{message}</p>;
}

export function ContactForm() {
  const { toast } = useToast();
  const [values, setValues] = useState<ContactFormValues>(INITIAL_VALUES);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setFieldValue = (field: ContactField, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validateContactForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error || 'Не удалось отправить сообщение. Попробуйте позже.');
      }

      toast({
        title: 'Сообщение отправлено',
        description: 'Мы получили сообщение и скоро ответим.',
      });

      setValues(INITIAL_VALUES);
      setErrors({});
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Сообщение не отправлено',
        description: error instanceof Error ? error.message : 'Не удалось отправить сообщение. Попробуйте позже.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="contact-name" className="text-sm font-medium leading-none">
            Имя
          </label>
          <Input
            id="contact-name"
            name="name"
            autoComplete="name"
            placeholder="Ваше имя"
            value={values.name}
            onChange={(event) => setFieldValue('name', event.target.value)}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'contact-name-error' : undefined}
          />
          <div id="contact-name-error">
            <FieldError message={errors.name} />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="contact-email" className="text-sm font-medium leading-none">
            Электронная почта
          </label>
          <Input
            id="contact-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="ivan@example.com"
            value={values.email}
            onChange={(event) => setFieldValue('email', event.target.value)}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'contact-email-error' : undefined}
          />
          <div id="contact-email-error">
            <FieldError message={errors.email} />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="contact-subject" className="text-sm font-medium leading-none">
          Тема
        </label>
        <select
          id="contact-subject"
          name="subject"
          value={values.subject}
          onChange={(event) => setFieldValue('subject', event.target.value)}
          className={cn(selectClassName, !values.subject && 'text-[var(--site-ink-subtle)]')}
          aria-invalid={Boolean(errors.subject)}
          aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
        >
          <option value="">Выберите тему</option>
          {SUBJECT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div id="contact-subject-error">
          <FieldError message={errors.subject} />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="contact-message" className="text-sm font-medium leading-none">
          Сообщение
        </label>
        <Textarea
          id="contact-message"
          name="message"
          placeholder="Чем мы можем помочь?"
          className="min-h-[160px]"
          value={values.message}
          onChange={(event) => setFieldValue('message', event.target.value)}
          aria-invalid={Boolean(errors.message)}
          aria-describedby={errors.message ? 'contact-message-error' : undefined}
        />
        <div id="contact-message-error">
          <FieldError message={errors.message} />
        </div>
      </div>

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? 'Отправляем...' : 'Отправить сообщение'}
      </Button>
    </form>
  );
}
